import { useId } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Chip } from '@/components/Chip/Chip';
import { Dialog } from '@/components/Dialog/Dialog';
import { Icon } from '@/components/Icon/Icon';
import type { LenderDocument, LenderDocumentVersion } from '@/graphql/lenders';
import { formatBytes, formatDate, formatDay } from '@/lib/format';

import styles from './DocumentHistory.module.css';

export interface DocumentHistoryProps {
  document: LenderDocument;
  lenderName: string;
  canEdit: boolean;
  /** The signed-in person, so their own versions read "You". */
  meId?: string | null;
  downloadingVersionId?: string | null;
  onClose: () => void;
  onReplace: () => void;
  onRetitle: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
  onRevert: (version: LenderDocumentVersion) => void;
  onDownload: (version: LenderDocumentVersion) => void;
}

function publisherOf(version: LenderDocumentVersion, meId: string | null | undefined): string {
  if (!version.createdById) {
    return 'LendWell';
  }
  return version.createdById === meId ? 'You' : 'A colleague';
}

/**
 * One document opened: what clients get today, what you can do to it, and
 * every version it has had. Restoring an older version moves clients back to
 * it without adding a version, so the list reads the same before and after.
 */
export function DocumentHistory({
  document,
  lenderName,
  canEdit,
  meId,
  downloadingVersionId = null,
  onClose,
  onReplace,
  onRetitle,
  onArchive,
  onRestore,
  onDelete,
  onRevert,
  onDownload,
}: DocumentHistoryProps) {
  const headingId = useId();
  const current = document.currentVersion;
  const archived = document.status === 'ARCHIVED';
  const title = current?.title ?? document.key ?? 'Document';

  return (
    <Dialog
      eyebrow={
        <>
          <Chip tone={archived ? 'warn' : 'good'}>{archived ? 'Archived' : 'Active'}</Chip>
          <Chip>{lenderName}</Chip>
        </>
      }
      onClose={onClose}
      placement="drawer"
      title={title}
    >
      {archived ? (
        <Callout title="Archived">
          Clients aren’t given this document. Restore it to make it available again.
        </Callout>
      ) : null}

      {canEdit ? (
        <div className={styles.actions}>
          <Button icon={<Icon name="upload-simple" />} onClick={onReplace} size="sm">
            Upload new version
          </Button>
          <Button
            icon={<Icon name="pencil-simple" />}
            onClick={onRetitle}
            size="sm"
            variant="ghost"
          >
            Rename
          </Button>
          {archived ? (
            <Button icon={<Icon name="eye" />} onClick={onRestore} size="sm" variant="ghost">
              Restore
            </Button>
          ) : (
            <Button icon={<Icon name="archive" />} onClick={onArchive} size="sm" variant="ghost">
              Archive
            </Button>
          )}
          <Button icon={<Icon name="trash" />} onClick={onDelete} size="sm" variant="quiet">
            Delete
          </Button>
        </div>
      ) : null}

      <section aria-labelledby={headingId} className={styles.history}>
        <h3 className={styles.heading} id={headingId}>
          Version history
          <span className={styles.count}>{document.versions.length}</span>
        </h3>
        <ol className={styles.timeline}>
          {document.versions.map((version) => {
            const isCurrent = version.id === document.currentVersionId;
            const label = `version ${version.versionNo ?? ''}`.trim();
            return (
              <li
                aria-current={isCurrent ? 'true' : undefined}
                className={`${styles.version} ${isCurrent ? styles.current : ''}`}
                key={version.id}
              >
                <span aria-hidden="true" className={styles.dot} />
                <div className={styles.card}>
                  <div className={styles.cardHead}>
                    <span className={styles.versionNo}>Version {version.versionNo}</span>
                    {isCurrent ? (
                      <Chip tone="good">{archived ? 'Latest' : 'Clients see this'}</Chip>
                    ) : null}
                  </div>
                  <p className={styles.versionTitle}>{version.title}</p>
                  <p className={styles.file}>
                    <Icon name="file" size="0.95rem" />
                    <span className={styles.filename}>{version.file?.filename ?? 'File'}</span>
                    {version.file?.sizeBytes != null ? (
                      <span className={styles.size}>{formatBytes(version.file.sizeBytes)}</span>
                    ) : null}
                  </p>
                  <p className={styles.byline}>
                    {publisherOf(version, meId)} ·{' '}
                    <time
                      dateTime={version.createdAt ?? undefined}
                      title={formatDate(version.createdAt)}
                    >
                      {formatDay(version.createdAt)}
                    </time>
                  </p>
                  <div className={styles.cardActions}>
                    <Button
                      aria-label={`Download ${label}`}
                      disabled={!version.fileId}
                      icon={<Icon name="download-simple" />}
                      loading={downloadingVersionId === version.id}
                      onClick={() => onDownload(version)}
                      size="sm"
                      variant="ghost"
                    >
                      Download
                    </Button>
                    {canEdit && !isCurrent ? (
                      <Button
                        aria-label={`Restore ${label}`}
                        icon={<Icon name="arrow-counter-clockwise" />}
                        onClick={() => onRevert(version)}
                        size="sm"
                        variant="quiet"
                      >
                        Restore this version
                      </Button>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <p className={styles.reference}>
        Reference <code className={styles.key}>{document.key}</code>
      </p>
    </Dialog>
  );
}
